import React from 'react';
import { Tooltip, withStyles } from '@material-ui/core';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { AdminDashboardPriceListCount } from '../../graphql/API';
import { icons } from '../../themes/icons';

type PriceListCount = Pick<AdminDashboardPriceListCount, 'priceList' | 'count'>;

interface PackageCardProps {
  bgColor: string;
  label: string;
  count?: string;
  comparison?: number;
  substr?: string;
  isMobile?: boolean;
  showFooter?: boolean;
  newCount?: number;
  canceledCount?: number;
  showComparison?: boolean;
  selectedLabel?: string;
  link?: string;
  // Split of the "Added" / "Canceled" chips by price list, shown on hover.
  newByPriceList?: PriceListCount[] | null;
  canceledByPriceList?: PriceListCount[] | null;
}

/** The chip as is, or with its split by price list on hover when there is one. */
const WithPriceListSplit: React.FC<{
  title: string;
  split?: PriceListCount[] | null;
  children: React.ReactElement;
}> = ({ title, split, children }) => {
  if (!split?.length) return children;
  return (
    <SplitTooltip
      arrow
      placement="bottom-start"
      title={
        <>
          <SplitTitle>{title}</SplitTitle>
          {split.map(({ priceList, count }) => (
            <SplitRow key={priceList}>
              <span>${priceList}</span>
              <SplitCount>{count}</SplitCount>
            </SplitRow>
          ))}
        </>
      }
    >
      {children}
    </SplitTooltip>
  );
};

export const PackageCard: React.FC<PackageCardProps> = ({
  label,
  count,
  substr = '',
  isMobile = false,
  showFooter = false,
  newCount,
  canceledCount,
  link,
  newByPriceList,
  canceledByPriceList,
}) => {
  // Premium/Scouting cards use the green/red "Added/Canceled" chips instead of the neutral ones
  const isSubscriptionCard = label === 'Premium models' || label === 'Scouting subscription';
  const note = substr ? `(${substr})` : '';
  const boxComponent = (
    <ActiveModals>
      <Heading>
        {label}
        {label?.includes('Starter') ? (
          <icons.StarterHatIcon />
        ) : label === 'Plus' ? (
          <icons.PremiumHatIcon />
        ) : label === 'Premium models' ? (
          <icons.PremiumHatIcon />
        ) : null}
      </Heading>
      {isMobile ? (
        <Count>
          {count}
          {note && <span>{note}</span>}
        </Count>
      ) : (
        <DesktopCardData>
          <Count>
            {count}
            {note && <span>{note}</span>}
          </Count>
        </DesktopCardData>
      )}
      {showFooter && (
        <FooterContainer>
          <WithPriceListSplit title="Added by price list" split={newByPriceList}>
            <ValueContainer chip={isSubscriptionCard ? 'positive' : 'neutral'}>
              <FooterLabel>{isSubscriptionCard ? 'Added' : 'New'}</FooterLabel>
              <FooterValue tone={isSubscriptionCard ? 'positive' : undefined}>
                {isSubscriptionCard ? `+${newCount}` : newCount}
              </FooterValue>
            </ValueContainer>
          </WithPriceListSplit>
          <WithPriceListSplit title="Canceled by price list" split={canceledByPriceList}>
            <ValueContainer chip={isSubscriptionCard ? 'negative' : 'neutral'}>
              <FooterLabel>Canceled</FooterLabel>
              <FooterValue tone={isSubscriptionCard ? 'negative' : undefined}>
                {isSubscriptionCard ? `-${canceledCount}` : canceledCount}
              </FooterValue>
            </ValueContainer>
          </WithPriceListSplit>
        </FooterContainer>
      )}
    </ActiveModals>
  );

  return (
    <>
      {link ? (
        <Link to={link} style={{ color: 'inherit', textDecoration: 'none', width: '100%' }}>
          {boxComponent}
        </Link>
      ) : (
        boxComponent
      )}
    </>
  );
};

const ActiveModals = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 10px 16px;
  width: 100%;
`;

const Heading = styled.span`
  color: #111111;
  font-size: 13px;
  font-family: Poppins;
  font-weight: 400;
  word-wrap: break-word;
  line-height: 18px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Count = styled.p`
  color: #111111;
  font-size: 36px;
  font-family: Poppins;
  font-weight: 500;
  word-wrap: break-word;
  line-height: 18px;
  margin: 20px 0 8px 0;

  span {
    font-size: 13px;
    font-family: Poppins;
    font-weight: 400;
    line-height: 18px;
    word-wrap: break-word;
    margin-left: 8px;
  }
`;

const DesktopCardData = styled.div`
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const FooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  font-size: 13px;
  margin-top: 5px;
`;

const chipBackground = (chip?: 'positive' | 'negative' | 'neutral') => {
  if (chip === 'positive') return '#E7F4EC';
  if (chip === 'negative') return '#FBEBEC';
  return '#F2F2F2';
};

const ValueContainer = styled.div<{ chip?: 'positive' | 'negative' | 'neutral' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  ${({ chip }) =>
    chip &&
    `
    padding: 3px 10px;
    border-radius: 6px;
    background: ${chipBackground(chip)};
  `}
`;

const FooterLabel = styled.span`
  display: inline-block;
  font-weight: 400;
`;

const FooterValue = styled.span<{ tone?: 'positive' | 'negative' }>`
  display: inline-block;
  font-weight: 500;
  color: ${({ tone }) =>
    tone === 'positive' ? '#408155' : tone === 'negative' ? '#BC3438' : 'inherit'};
`;

const SplitTooltip = withStyles({
  tooltip: {
    backgroundColor: '#ffffff',
    color: '#232323',
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 400,
    minWidth: 160,
    padding: '10px 14px',
    borderRadius: 8,
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
  },
  arrow: {
    color: '#ffffff',
  },
})(Tooltip);

const SplitTitle = styled.div`
  color: #848484;
  margin-bottom: 4px;
`;

const SplitRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
`;

const SplitCount = styled.span`
  font-weight: 500;
`;
